import { apiClient } from './client';
import { unwrapAndTransform } from '../utils/api-response';
// ---------------------------------------------------------------------------
// All transformation now handled by unwrapAndTransform
// Eliminates: custom transformAgent function + manual unwrapping patterns
// ---------------------------------------------------------------------------
export const agentApi = {
    // List all agents
    listAgents: async () => {
        const response = await apiClient.get('/v1/admin/cortex/agents');
        return unwrapAndTransform(response);
    },
    // Get agent details
    getAgent: async (id) => {
        const response = await apiClient.get(`/v1/admin/cortex/agents/${id}`);
        return unwrapAndTransform(response);
    },
    // List agent tasks
    listAgentTasks: async (_agentId, params) => {
        // Cortex doesn't have a tasks endpoint for agents
        // Return empty result to avoid 502 errors
        return {
            tasks: [],
            total: 0,
            page: params?.page || 1,
            perPage: params?.perPage || 50,
        };
    },
    // Get conversation
    getConversation: async (agentId, conversationId) => {
        const response = await apiClient.get(`/v1/admin/cortex/agents/${agentId}/conversations/${conversationId}`);
        return unwrapAndTransform(response);
    },
    // List workflow definitions
    listWorkflowDefinitions: async () => {
        const response = await apiClient.get('/v1/admin/cortex/workflow-definitions');
        const workflows = unwrapAndTransform(response);
        // Transform API response to match frontend types
        // Note: This transformation logic is specific to workflow structure
        // and cannot be auto-handled by snake_case conversion
        return workflows.map((workflow) => {
            const nodes = workflow.definition?.nodes || [];
            // Convert nodes to steps format expected by frontend
            const steps = nodes
                .filter((node) => node.type === 'llm_call')
                .map((node) => ({
                id: node.id,
                name: node.id,
                type: node.type,
                agentId: node.config?.agentId || null,
                dependencies: [],
                config: node.config || {},
            }));
            return {
                id: workflow.id,
                name: workflow.name,
                description: workflow.description,
                version: workflow.metadata?.version || '1.0.0',
                steps,
                lastRun: workflow.lastRun,
                runCount: workflow.runCount || 0,
                createdAt: workflow.createdAt,
                updatedAt: workflow.updatedAt,
            };
        });
    },
    // Get workflow definition
    getWorkflowDefinition: async (id) => {
        const response = await apiClient.get(`/v1/admin/cortex/workflow-definitions/${id}`);
        return unwrapAndTransform(response);
    },
    // Trigger workflow manually
    triggerWorkflow: async (id, context) => {
        const response = await apiClient.post(`/v1/admin/cortex/workflow-definitions/${id}/run`, { context });
        return unwrapAndTransform(response);
    },
    // Update agent
    updateAgent: async (id, data) => {
        const payload = {};
        if (data.name !== undefined)
            payload.name = data.name;
        if (data.description !== undefined)
            payload.description = data.description;
        if (data.systemPrompt !== undefined)
            payload.systemPrompt = data.systemPrompt;
        if (data.model !== undefined)
            payload.model = data.model;
        // Only include modelsByTier if it has at least one tier defined
        if (data.modelsByTier !== undefined && data.modelsByTier !== null) {
            const hasTiers = data.modelsByTier.freemium || data.modelsByTier.pro || data.modelsByTier.premium;
            if (hasTiers) {
                payload.modelsByTier = data.modelsByTier;
            }
        }
        if (data.temperature !== undefined)
            payload.temperature = data.temperature;
        if (data.maxTokens !== undefined)
            payload.maxTokens = data.maxTokens;
        if (data.topP !== undefined)
            payload.topP = data.topP;
        if (data.status !== undefined)
            payload.status = data.status;
        if (data.skills !== undefined)
            payload.skills = data.skills;
        const response = await apiClient.put(`/v1/admin/cortex/agents/${id}`, payload);
        return unwrapAndTransform(response);
    },
    // Get available models
    getAvailableModels: async (tier) => {
        const params = tier ? { tier } : {};
        const response = await apiClient.get('/v1/admin/cortex/models', { params });
        return unwrapAndTransform(response);
    },
};
